import type { Capability } from '@/api/schema'

/**
 * Personas do modo mock (dev/testes). Espelham a matriz inicial do DEC-004; a fonte de
 * verdade real é o backend (GET /api/v2/me). `none` = autenticado sem nenhuma permissão.
 */
export const MOCK_ROLES = ['viewer', 'operator', 'admin', 'none'] as const
export type MockRole = (typeof MOCK_ROLES)[number]

const VIEWER: Capability[] = [
  'schedule:read',
  'metabase:read',
  'integration:read',
  'realtime:subscribe',
]
// TD-015: disparar verificação de integração é ação de operator.
const OPERATOR: Capability[] = [...VIEWER, 'schedule:create', 'integration:run']
const ADMIN: Capability[] = [...OPERATOR, 'metabase:manage', 'system:admin']

export const MOCK_ROLE_CAPABILITIES: Record<MockRole, Capability[]> = {
  viewer: VIEWER,
  operator: OPERATOR,
  admin: ADMIN,
  none: [],
}

export const MOCK_PERSONAS: Record<MockRole, { name: string; email: string; username: string }> = {
  viewer: { name: 'Vera Viewer', email: 'viewer@woctools.local', username: 'viewer' },
  operator: { name: 'Otávio Operador', email: 'operator@woctools.local', username: 'operator' },
  admin: { name: 'Ana Admin', email: 'admin@woctools.local', username: 'admin' },
  none: { name: 'Sem Perfil', email: 'none@woctools.local', username: 'none' },
}

export const MOCK_TOKEN_PREFIX = 'mock.'
export const mockTokenFor = (role: MockRole) => `${MOCK_TOKEN_PREFIX}${role}`

export function roleFromMockToken(authorization: string | null): MockRole | null {
  const token = authorization?.replace(/^Bearer\s+/i, '') ?? ''
  if (!token.startsWith(MOCK_TOKEN_PREFIX)) return null
  const role = token.slice(MOCK_TOKEN_PREFIX.length)
  return (MOCK_ROLES as readonly string[]).includes(role) ? (role as MockRole) : null
}

export const isMockRole = (value: string | null | undefined): value is MockRole =>
  !!value && (MOCK_ROLES as readonly string[]).includes(value)

export const MOCK_ROLE_STORAGE_KEY = 'woctools-mock-role'
const DEFAULT_ROLE: MockRole = 'admin'

/** Persona escolhida por `?mock_role=viewer` (persistida na sessão do navegador). */
export function resolveMockRole(search = window.location.search): MockRole {
  const fromQuery = new URLSearchParams(search).get('mock_role')
  try {
    if (isMockRole(fromQuery)) {
      window.sessionStorage.setItem(MOCK_ROLE_STORAGE_KEY, fromQuery)
      return fromQuery
    }
    const stored = window.sessionStorage.getItem(MOCK_ROLE_STORAGE_KEY)
    if (isMockRole(stored)) return stored
  } catch {
    if (isMockRole(fromQuery)) return fromQuery
  }
  return DEFAULT_ROLE
}
